import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// formik
import * as Yup from 'yup';
import { Formik } from 'formik';
// core components
import Card from 'components/UI/Card/Card';
import CardBody from 'components/UI/Card/CardBody';
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import GridContainer from 'components/UI/Grid/GridContainer';
import CustomInput from 'components/UI/CustomInput/CustomInput';
// our components
import PermissionSelect from 'components/Permission/Select';
// Helper
import RouterContext from 'helpers/routerContext';
// our routes
import { PERMISSION } from 'routes/paths';
import Loading from 'components/common/Loading/Loading';

/**
 * @description
 * Validation form's rules.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */
const translatedValidationSchema = t =>
  Yup.object({
    name: Yup.string().required(t('errors.required')),
  });

/**
 * @class
 * @extends React.Component
 */
class PermissionGroupForm extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      name: props.name,
      selectedPermissions: props.selectedPermissions,
    };
    this.formikRef = null;
  }

  /**
   * @description
   * Callback called when submit form.
   *
   * @param {Object} obj - Object with input values.
   * @returns {undefined} No return.
   */
  handleSubmitForm = ({ name }) => {
    const { permissionSave } = this.props;
    const { selectedPermissions } = this.state;
    const ids = selectedPermissions.map(({ id }) => id);
    permissionSave({ name, permissions: ids });
  };

  /**
   * @description
   * Callback called each time the permissions are selected.
   *
   * @param {Array} permissions - Updated list of selected permissions.
   */
  handleSelectPermissions = permissions => {
    this.setState({ selectedPermissions: permissions });
  };

  /**
   * @description
   * Set server errors with Formik setErrors function.
   *
   * @param {Object} serverErrors - Api errors.
   * @returns {Object} Global form's errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    history.push(PERMISSION.LIST);
  };

  /**
   * @description
   * Render Create PermissionGroup form using Formik.
   */
  render() {
    const { errors: serverErrors, editMode, loading, t } = this.props;
    const all = this.setServerErrors(serverErrors);
    const validationSchema = translatedValidationSchema(t);

    const { name, selectedPermissions } = this.state;
    return (
      <React.Fragment>
        <Snackbars place="tc" message={all || ''} open={!!all} color="danger" />
        <Formik
          ref={ref => {
            this.formikRef = ref;
          }}
          initialValues={{ name }}
          validationSchema={validationSchema}
          onSubmit={this.handleSubmitForm}
          render={({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
          }) => (
            <form onSubmit={handleSubmit}>
              <Card>
                <Loading show={loading} />
                <CardBody>
                  <GridContainer>
                    <GridItem xs={12} sm={5}>
                      <CustomInput
                        labelText={t('permissions.form.placeholder')}
                        id="name"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        helpText={touched.name ? errors.name : ''}
                        error={touched.name && Boolean(errors.name)}
                        inputProps={{
                          value: values.name,
                          onChange: e => {
                            handleChange(e);
                          },
                          onBlur: handleBlur,
                          type: 'text',
                        }}
                        updatePermissions
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <PermissionSelect
                        selectedPermissions={selectedPermissions}
                        permissionUpdate={this.handleSelectPermissions}
                      />
                    </GridItem>
                  </GridContainer>

                  <GridContainer direction="row-reverse">
                    <GridItem xs={12} sm={6}>
                      <GridContainer justify="flex-end">
                        <GridItem xs={12} sm={6}>
                          <Button
                            id="btn-submit-group"
                            type="submit"
                            color="primary"
                            block
                          >
                            {editMode
                              ? t('permissions.form.edit')
                              : t('permissions.form.create')}
                          </Button>
                        </GridItem>
                      </GridContainer>
                    </GridItem>

                    <GridItem xs={12} sm={6}>
                      <GridContainer>
                        <GridItem xs={12} sm={6}>
                          <Button
                            id="btn-cancel-group"
                            onClick={this.handleCancelBtn}
                            simple
                            color="danger"
                            block
                          >
                            {t('common.cancel')}
                          </Button>
                        </GridItem>
                      </GridContainer>
                    </GridItem>
                  </GridContainer>
                </CardBody>
              </Card>
            </form>
          )}
        />
      </React.Fragment>
    );
  }
}

/**
 * PermissionForm PropTypes
 * @param errors - prop errors list received from parents
 * @param t - prop from HOC with string translations
 * @callback createGroup - callback called when validations are success
 */

PermissionGroupForm.propTypes = {
  name: PropTypes.string,
  selectedPermissions: PropTypes.instanceOf(Array),
  errors: PropTypes.instanceOf(Array).isRequired,
  permissionSave: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

PermissionGroupForm.defaultProps = {
  name: '',
  selectedPermissions: [],
  editMode: false,
};

export default withTranslation()(PermissionGroupForm);
